import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useOpenWizard } from "@/lib/wizard-navigation";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import {
  StatusBadge,
  conferenceStatusTone,
} from "@/components/status/status-badge";
import { api } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import type { Conference, Destination } from "@/lib/types";

type Filter =
  | "all"
  | "upcoming"
  | "active"
  | "completed"
  | "needs_attention"
  | "not_configured";

const FILTERS: Filter[] = [
  "all",
  "upcoming",
  "active",
  "completed",
  "needs_attention",
  "not_configured",
];

function isFilter(value: string | null): value is Filter {
  return value !== null && (FILTERS as string[]).includes(value);
}

export function ConferencesPage() {
  const { orgId, canManage } = useOrg();
  const openWizard = useOpenWizard();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conferences, setConferences] = useState<Conference[]>([]);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const filter: Filter = isFilter(searchParams.get("filter"))
    ? searchParams.get("filter")!
    : "all";
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  function setFilter(next: Filter) {
    if (next === "all") {
      setSearchParams({}, { replace: true });
      return;
    }
    setSearchParams({ filter: next }, { replace: true });
  }

  useEffect(() => {
    setLoading(true);
    void Promise.all([
      api<{ conferences: Conference[] }>(`/api/accounts/${orgId}/conferences`),
      api<{ streams: Destination[] }>(`/api/accounts/${orgId}/dashboard`),
    ])
      .then(([c, d]) => {
        setConferences(c.conferences);
        setDestinations(d.streams ?? []);
      })
      .catch(() => {
        setConferences([]);
        setDestinations([]);
      })
      .finally(() => setLoading(false));
  }, [orgId]);

  const filtered = useMemo(() => {
    return conferences.filter((c) => {
      if (q && !c.name.toLowerCase().includes(q.toLowerCase())) return false;
      const dests = destinations.filter((d) => d.conferenceId === c.id);
      const failing = dests.some((d) => d.lastErrorCode);
      switch (filter) {
        case "upcoming":
          return c.status === "draft" || c.status === "trial";
        case "active":
          return c.status === "active" || c.status === "trial";
        case "completed":
          return c.status === "inactive" || c.status === "cancelled";
        case "needs_attention":
          return failing || c.status === "past_due";
        case "not_configured":
          return (
            dests.length === 0 || dests.every((d) => d.lifecycle === "draft")
          );
        default:
          return true;
      }
    });
  }, [conferences, destinations, filter, q]);

  if (loading) return <PageSkeleton />;

  if (conferences.length === 0) {
    return (
      <EmptyState
        title="Select a Conference"
        description="Connect Zoom (if needed), then pick a meeting or webinar to start syncing registrants."
        actionLabel={canManage ? "Select a Conference" : undefined}
        onAction={canManage ? () => openWizard() : undefined}
      />
    );
  }

  const filters: { id: Filter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "upcoming", label: "Upcoming" },
    { id: "active", label: "Active" },
    { id: "not_configured", label: "Not configured" },
    { id: "needs_attention", label: "Needs attention" },
    { id: "completed", label: "Completed" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-rs-ink">Conferences</h2>
          <p className="text-sm text-rs-ink-muted">
            Billable events and their destination sync status.
          </p>
        </div>
        {canManage ? (
          <Button onClick={() => openWizard()}>Add Conference</Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="max-w-xs"
          placeholder="Search conferences…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search Conferences"
        />
        <div className="flex flex-wrap gap-1">
          {filters.map((f) => (
            <Button
              key={f.id}
              size="sm"
              variant={filter === f.id ? "primary" : "secondary"}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      <Panel className="overflow-hidden">
        <div className="hidden grid-cols-[2fr_1fr_1fr_1fr] gap-3 border-b border-rs-border bg-rs-muted px-5 py-2 text-xs font-semibold uppercase tracking-wide text-rs-ink-subtle md:grid">
          <span>Name</span>
          <span>Status</span>
          <span>Destinations</span>
          <span>Billing</span>
        </div>
        <ul className="divide-y divide-rs-border">
          {filtered.map((c) => {
            const dests = destinations.filter((d) => d.conferenceId === c.id);
            return (
              <li key={c.id}>
                <Link
                  to={`/conferences/${c.id}`}
                  className="grid gap-2 px-5 py-3 hover:bg-rs-muted md:grid-cols-[2fr_1fr_1fr_1fr] md:items-center"
                >
                  <span className="font-medium text-rs-ink">{c.name}</span>
                  <StatusBadge tone={conferenceStatusTone(c.status)}>
                    {c.status}
                  </StatusBadge>
                  <span className="text-sm text-rs-ink-muted">
                    {dests.length
                      ? dests.map((d) => d.destinationProvider).join(", ")
                      : "None"}
                  </span>
                  <span className="text-sm capitalize text-rs-ink-muted">
                    {c.status}
                  </span>
                </Link>
              </li>
            );
          })}
          {filtered.length === 0 ? (
            <li className="px-5 py-10 text-center text-sm text-rs-ink-muted">
              No conferences match this filter.
            </li>
          ) : null}
        </ul>
      </Panel>
    </div>
  );
}
